import { parseConfig } from './config';
import { Sidebar } from './sidebar';
import { Onboarding } from './onboarding';
import { Executor, setupAutoResume } from './executor';
import { Widget } from './widget';
import type { WarpwayConfig } from './types';

/**
 * Main Warpway SDK class
 */
class WarpwaySDK {
  private config: WarpwayConfig;
  private sidebar: Sidebar;
  private onboarding: Onboarding;
  private widget: Widget | null = null;

  constructor() {
    this.config = parseConfig();
    this.sidebar = new Sidebar(this.config.siteId);
    this.onboarding = new Onboarding();

    this.init();
  }

  /**
   * Initialize the SDK
   */
  private async init(): Promise<void> {
    console.log('[Warpway SDK] Initializing...', {
      devMode: this.config.devMode,
      widgetEnabled: this.config.widgetEnabled,
      siteId: this.config.siteId || 'not set',
    });

    // Send beacon to track SDK installation
    if (this.config.siteId) {
      this.sendBeacon();
    }

    // Setup auto-resume for executor (handles hard reloads during test execution)
    setupAutoResume();

    // Mount the sidebar
    this.sidebar.mount();

    // Set onboarding content
    this.sidebar.setContent(this.onboarding.getElement());

    // Show sidebar only in dev mode
    if (this.config.devMode) {
      this.sidebar.show();
      this.sidebar.open();
      console.log('[Warpway SDK] Dev mode active - sidebar enabled');
    } else {
      this.sidebar.hide();
    }

    // Initialize end-user widget if enabled (and not in dev mode)
    if (this.config.widgetEnabled && !this.config.devMode && this.config.siteId) {
      console.log('[Warpway SDK] Initializing end-user widget...');
      await this.initWidget();
    }
  }

  /**
   * Fetch widget config from API and initialize widget
   */
  private async initWidget(): Promise<void> {
    try {
      const apiBase = this.getApiBase();
      const response = await fetch(`${apiBase}/v1/sites/${this.config.siteId}/widget-config`, {
        headers: {
          'x-warpway-site-id': this.config.siteId!,
        },
      });

      if (!response.ok) {
        console.error('[Warpway SDK] Failed to fetch widget config:', response.status);
        // Fall back to basic config
        this.widget = new Widget({
          siteId: this.config.siteId!,
          enabled: true,
        });
        this.widget.mount();
        return;
      }

      const widgetConfig = await response.json();

      // Create widget with config from API
      this.widget = new Widget({
        siteId: this.config.siteId!,
        enabled: widgetConfig.enabled ?? true,
        brandColor: widgetConfig.brandColor,
        title: widgetConfig.title,
        iconType: widgetConfig.iconType,
        logoUrl: widgetConfig.logoUrl,
        position: widgetConfig.position,
        offsetX: widgetConfig.offsetX,
        offsetY: widgetConfig.offsetY,
        zIndex: widgetConfig.zIndex,
        hotkeyEnabled: widgetConfig.hotkeyEnabled,
        hotkeyCombo: widgetConfig.hotkeyCombo,
        executionSpeed: widgetConfig.executionSpeed,
        showAnimations: widgetConfig.showAnimations,
        autoOpen: widgetConfig.autoOpen,
        plan: widgetConfig.plan,
      });
      this.widget.mount();
    } catch (error) {
      console.error('[Warpway SDK] Error initializing widget:', error);
      // Fall back to basic config
      this.widget = new Widget({
        siteId: this.config.siteId!,
        enabled: true,
      });
      this.widget.mount();
    }
  }

  /**
   * Send beacon to API to track SDK installation and activity
   */
  private sendBeacon(): void {
    const apiBase = this.getApiBase();
    const origin = window.location.origin;

    // Use fetch with keepalive for reliable beacon
    const beaconUrl = `${apiBase}/v1/sites/${this.config.siteId}/ping?origin=${encodeURIComponent(origin)}`;

    fetch(beaconUrl, {
      method: 'GET',
      mode: 'no-cors', // Allow cross-origin without CORS preflight
      keepalive: true, // Ensure request completes even if page unloads
    }).catch((err) => {
      // Silently fail - beacon is best-effort
      console.debug('[Warpway SDK] Beacon failed:', err);
    });
  }

  /**
   * Get API base URL based on environment
   */
  private getApiBase(): string {
    // Check if running in dev mode (localhost)
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      return 'http://localhost:8000';
    }

    // Production
    return 'https://api.warpway.ai';
  }

  /**
   * Public API: Get current configuration
   */
  getConfig(): WarpwayConfig {
    return { ...this.config };
  }

  /**
   * Public API: Toggle sidebar
   */
  toggleSidebar(): void {
    this.sidebar.toggle();
  }

  /**
   * Public API: Open the widget chat window
   */
  openWidget(): void {
    if (this.widget) {
      this.widget.open();
    } else {
      console.warn('[Warpway SDK] Widget not initialized');
    }
  }

  /**
   * Public API: Close the widget chat window
   */
  closeWidget(): void {
    if (this.widget) {
      this.widget.close();
    } else {
      console.warn('[Warpway SDK] Widget not initialized');
    }
  }

  /**
   * Public API: Toggle the widget chat window
   */
  toggleWidget(): void {
    if (this.widget) {
      this.widget.toggle();
    } else {
      console.warn('[Warpway SDK] Widget not initialized');
    }
  }
}

// Auto-initialize when script loads
let sdkInstance: WarpwaySDK | null = null;

function initialize() {
  if (sdkInstance) {
    console.warn('[Warpway SDK] Already initialized');
    return;
  }

  sdkInstance = new WarpwaySDK();

  // Expose to window for debugging (dev mode only)
  if (typeof window !== 'undefined') {
    (window as any).__warpway__ = sdkInstance;
  }
}

// Run initialization
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initialize);
} else {
  initialize();
}

// Export for potential module usage
export default WarpwaySDK;
export { Executor } from './executor';
export { Widget } from './widget';
export type { ExecutorConfig, ExecutionResult, StepResult } from './executor';
export type { WidgetConfig } from './widget';
